import { CdkDragHandle } from '@angular/cdk/drag-drop';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';

import type { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import type { Attachment } from '@core/attachments-types';
import { I18nService } from '@core/i18n-legacy';
import { injectSetRecruiteeFilteredMentions, provideFilteredMentions } from '@core/notes-legacy';
import { CandidateEvaluationSettingsDataService } from '@recruitee/candidate-evaluation-settings-logic';
import {
  EvaluationAttachmentsService,
  EvaluationFormSubmissionService,
  EvaluationPanelActionsService,
  EvaluationPanelClearService,
  EvaluationPanelContentLoaderService,
  EvaluationPanelDraftAutosaveService,
  EvaluationPanelStateService,
  EvaluationPanelSubmitService,
  QuickEvaluationFormService,
  TemplateEvaluationFormService,
} from '@recruitee/evaluations-logic';
import type {
  EvaluationPanelCandidate,
  EvaluationPanelData,
  EvaluationPanelMentionUsers,
  EvaluationPanelOffer,
  QuickEvaluationFormValue,
  TemplateEvaluationFormValue,
} from '@recruitee/evaluations-types';
import {
  EvaluationPanelFooterComponent,
  EvaluationPanelHeaderComponent,
  EvaluationPanelSkeletonComponent,
} from '@recruitee/evaluations-ui';

import { QuickEvaluationFormComponent } from './quick-evaluation-form/quick-evaluation-form.component';
import { TemplateEvaluationFormComponent } from './template-evaluation-form/template-evaluation-form.component';

@Component({
  selector: 'rt-evaluation-floating-panel',
  templateUrl: './evaluation-floating-panel.component.html',
  styleUrls: ['./evaluation-floating-panel.component.less'],
  imports: [
    CdkDragHandle,
    EvaluationPanelHeaderComponent,
    EvaluationPanelFooterComponent,
    EvaluationPanelSkeletonComponent,
    QuickEvaluationFormComponent,
    TemplateEvaluationFormComponent,
  ],
  providers: [
    EvaluationPanelActionsService,
    EvaluationPanelClearService,
    EvaluationPanelContentLoaderService,
    EvaluationPanelDraftAutosaveService,
    EvaluationPanelSubmitService,
    EvaluationFormSubmissionService,
    QuickEvaluationFormService,
    TemplateEvaluationFormService,
    provideFilteredMentions(),
  ],
})
export class EvaluationFloatingPanelComponent {
  private readonly i18n = inject(I18nService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly evaluationPanelActionsService = inject(EvaluationPanelActionsService);
  private readonly evaluationPanelStateService = inject(EvaluationPanelStateService);
  private readonly quickEvaluationFormService = inject(QuickEvaluationFormService);
  private readonly templateEvaluationFormService = inject(TemplateEvaluationFormService);
  private readonly evaluationFormSubmissionService = inject(EvaluationFormSubmissionService);
  private readonly evaluationAttachmentsService = inject(EvaluationAttachmentsService);
  private readonly clearService = inject(EvaluationPanelClearService);
  private readonly contentLoader = inject(EvaluationPanelContentLoaderService);
  private readonly draftAutosave = inject(EvaluationPanelDraftAutosaveService);
  private readonly submitService = inject(EvaluationPanelSubmitService);
  private readonly settingsData = inject(CandidateEvaluationSettingsDataService);
  private readonly setMentions = injectSetRecruiteeFilteredMentions();

  private readonly quickFormOfferId = toSignal(
    this.quickEvaluationFormService.form.controls.offerId.valueChanges,
    { initialValue: this.quickEvaluationFormService.form.controls.offerId.value },
  );

  private readonly translations = {
    quickEvaluation: this.i18n.translate('app.evaluation_panel.quick_evaluation'),
  };

  protected readonly panelState = this.evaluationPanelStateService;
  protected readonly isSubmitting = this.evaluationPanelActionsService.isSubmitting;
  protected readonly isLoadingContent = this.evaluationPanelActionsService.isLoading;
  protected readonly attachmentsUploading = this.evaluationAttachmentsService.isUploading;
  protected readonly templateInfo = this.contentLoader.templateInfo;
  protected readonly draftSaved = this.draftAutosave.draftSaved;
  protected readonly templateForm = viewChild(TemplateEvaluationFormComponent);

  public readonly data = input.required<EvaluationPanelData>();
  public readonly candidate = input<EvaluationPanelCandidate | null>(null);
  public readonly availableOffers = input<EvaluationPanelOffer[]>([]);
  public readonly mentionUsers = input<EvaluationPanelMentionUsers | null>(null);
  public readonly restore = input<QuickEvaluationFormValue | TemplateEvaluationFormValue | null>(
    null,
  );
  public readonly close = output<void>();

  protected candidateOffers = computed(
    () =>
      this.candidate()
        ?.placements.map(placement => placement.offer || placement.talentPool)
        .filter(offer => !!offer) ?? [],
  );

  protected headerTitle = computed(() =>
    this.data().quickEvaluation
      ? this.translations.quickEvaluation
      : this.contentLoader.templateName(),
  );
  protected isQuickEditMode = computed(() => {
    const data = this.data();
    return !!data.quickEvaluation && !!data.edit && !!data.resultId;
  });
  protected isTemplateEditMode = computed(() => {
    const data = this.data();
    return !data.quickEvaluation && !!data.edit && !!data.resultId;
  });
  protected submitLabelKey = computed(() =>
    this.isQuickEditMode() || this.isTemplateEditMode()
      ? 'app.common.save'
      : 'app.evaluation_panel.add_evaluation',
  );
  protected ratingScale = computed(() => {
    const defaultScale = this.settingsData.defaultRatingScale() ?? null;

    if (this.isQuickEditMode() || this.isTemplateEditMode()) {
      return this.contentLoader.editedResult()?.ratingScale ?? defaultScale;
    }

    return defaultScale;
  });
  protected attachmentsByAnswerId = computed(() => {
    const result = this.contentLoader.editedResult();
    if (!result) return new Map<number, Attachment[]>();

    return new Map<number, Attachment[]>(
      result.interviewResultAnswers
        .filter(answer => !!answer.attachments?.length)
        .map(answer => [answer.id, <Attachment[]>answer.attachments]),
    );
  });
  protected currentOffer = computed(() => {
    const offerId = this.data().quickEvaluation
      ? (this.quickFormOfferId() ?? this.data().offerId)
      : this.data().offerId;
    return this.candidateOffers().find(offer => offer.id === offerId);
  });
  protected showSkeleton = computed(() => {
    if (!this.candidate()) return true;
    if (this.isLoadingContent()) return true;
    if (this.restore() && !this.draftAutosave.restoreApplied()) return true;
    if (this.data().quickEvaluation) return false;
    return !this.contentLoader.templateApplied();
  });
  protected quickPreselectedOfferId = computed(() => {
    const data = this.data();
    const restore = this.restore();
    if (data.quickEvaluation && restore) {
      return (<QuickEvaluationFormValue>restore).offerId ?? data.offerId;
    }
    return data.offerId;
  });

  private previousData: EvaluationPanelData | undefined;

  private _populateMentions = effect(() => {
    const users = this.mentionUsers();
    this.setMentions(users?.usersWithAccess ?? [], users?.usersWithoutAccess ?? []);
  });

  private _resetFormStateOnDataChange = effect(() => {
    const data = this.data();

    untracked(() => {
      if (this.previousData && this.previousData !== data) {
        this.resetFormState(data);
      }
      this.previousData = data;
    });
  });

  private _loadEditedQuickEvaluation = effect(() => {
    this.data();

    untracked(() =>
      this.contentLoader.loadEditedQuick(this.data, () => this.draftAutosave.applyRestore()),
    );
  });

  private _loadEditedTemplateEvaluation = effect(() => {
    this.data();

    untracked(() => this.contentLoader.loadEditedTemplate(this.data));
  });

  private _loadTemplate = effect(() => {
    const data = this.data();
    const offer = this.currentOffer();

    untracked(() => this.contentLoader.loadTemplate(data, offer));
  });

  private _candidateContextGuard = effect(() => {
    const candidate = this.candidate();
    const data = untracked(this.data);

    if (!candidate) return;

    if (candidate.id !== data.candidateId) {
      this.close.emit();
      return;
    }

    this.handleCandidateOffersChange(candidate, data);
  });

  private _restoreDraft = effect(() => {
    const restore = this.restore();
    const data = this.data();
    const candidate = this.candidate();
    const templateReady = this.contentLoader.templateApplied();

    if (!restore || untracked(() => this.draftAutosave.restoreApplied())) return;

    const isQuickEdit = !!data.quickEvaluation && !!data.edit && !!data.resultId;
    const canRestoreQuick = !!data.quickEvaluation && !isQuickEdit && !!candidate;
    const canRestoreTemplate = !data.quickEvaluation && templateReady;

    if (canRestoreQuick || canRestoreTemplate) {
      untracked(() => this.draftAutosave.applyRestore());
    }
  });

  constructor() {
    this.draftAutosave.connect(this.data, this.restore);
  }

  public confirmReplace(): Observable<boolean> {
    const data = this.data();
    const isQuick = !!data.quickEvaluation;

    return this.clearService.confirmDiscard(isQuick).pipe(
      tap(confirmed => {
        if (confirmed) this.clearService.clear(isQuick, data.candidateId);
      }),
    );
  }

  protected onClearForm(): void {
    const data = this.data();
    this.clearService.clearWithConfirm(!!data.quickEvaluation, data.candidateId);
  }

  protected onCancel(): void {
    const data = this.data();
    this.clearService
      .confirmDiscard(!!data.quickEvaluation)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(confirmed => {
        if (!confirmed) return;

        this.clearService.clear(!!data.quickEvaluation, data.candidateId);
        this.close.emit();
      });
  }

  protected onSubmit(): void {
    const result$ = this.submitService.submit(
      this.data(),
      this.candidate(),
      this.contentLoader.templateName(),
    );

    result$?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(success => {
      if (success) this.close.emit();
    });
  }

  private resetFormState(data: EvaluationPanelData): void {
    this.contentLoader.reset();
    this.draftAutosave.reset();
    this.evaluationFormSubmissionService.reset();
    this.quickEvaluationFormService.reset();

    if (data.quickEvaluation) {
      const [firstOffer] = this.availableOffers();
      const offerId = data.offerId ?? (firstOffer ? Number(firstOffer.id) : undefined);

      this.quickEvaluationFormService.init(offerId, this.candidate()?.placements ?? []);
      return;
    }

    this.templateEvaluationFormService.reset();
  }

  private handleCandidateOffersChange(
    candidate: EvaluationPanelCandidate,
    data: EvaluationPanelData,
  ): void {
    if (!data.quickEvaluation) {
      if (data.offerId && !this.candidateOffers().some(offer => offer.id === data.offerId)) {
        this.close.emit();
      }
      return;
    }

    const availableOffers = this.availableOffers();
    const selectedOfferId = this.quickEvaluationFormService.form.controls.offerId.value;
    if (!selectedOfferId || availableOffers.some(offer => offer.id === selectedOfferId)) return;

    const [firstOffer] = availableOffers;
    if (firstOffer) {
      this.quickEvaluationFormService.init(Number(firstOffer.id), candidate.placements);
    } else {
      this.quickEvaluationFormService.form.patchValue({ offerId: null, stageId: null });
    }
  }
}
